import { Phone, PhoneCall, PhoneMissed, PhoneOff, Play, Download, Mic, Loader2, ChevronDown, ChevronRight, X, PhoneOutgoing } from "lucide-react";
import { useState, useMemo } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { toast } from "@/app/_module/hooks/use-toast";
import type { CallRecord } from "@/app/_module/types/communication";
import { getDialerModeMeta } from "../utils/dialer-mode";
import {
  formatDuration,
  formatRelativeTime,
  formatPhoneNumber,
} from "@/app/_module/utils/formatters";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { getProxyRecordingUrl, downloadRecording } from "@/app/_module/lib/utils";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";
import { CallTranscriptPanel } from "@/app/(authenticated)/leads/_modules/components/CallTranscriptPanel";
import { RecordingPlayer } from "@/app/_module/components/RecordingPlayer";
import type { CallRecording } from "@/app/_module/contexts/CallRecordingContext";
import { ViewLeadButton } from "./ViewLeadButton";

interface CallListProps {
  calls: CallRecord[];
}

// A call row as the app-wide player's recording (CallRecordingContext).
const toRecording = (call: CallRecord & { recordingUrl: string }): CallRecording => ({
  callId: call.id,
  recordingUrl: call.recordingUrl,
  title: call.contactName || formatPhoneNumber(call.phoneNumber) || "Unknown caller",
  direction: call.direction === "inbound" ? "inbound" : "outbound",
  callDate: call.timestamp,
  durationSecs: call.duration,
  leadId: call.leadId,
});

// Constants for batch download
const MAX_BATCH_SIZE = 50;
const DOWNLOAD_DELAY = 500; // ms between downloads to avoid browser blocking

export const CallList = function({ calls }: CallListProps) {
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  // Batch selection state
  const [selectedCallIds, setSelectedCallIds] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [callingBack, setCallingBack] = useState<string | null>(null);

  // Group consecutive inbound missed/no-answer calls from the same phone number
  // within a 60s window. Customers who rapid-redial (hang up, instantly dial again,
  // sometimes 2-5 times in under a minute, often due to iPhone/carrier auto-retry)
  // previously created multiple identical rows in Recent Calls that agents
  // perceived as duplicates. Raw call_histories data is untouched — only display
  // collapses, with a "×N redials" badge showing the real count.
  const groupedCalls = useMemo(() => {
    const REDIAL_WINDOW_MS = 60 * 1000;
    const MISSED_STATUSES = new Set(['missed', 'no-answer', 'no_answer']);
    const isInboundMissed = (c: CallRecord): boolean =>
      c.direction === 'inbound' && MISSED_STATUSES.has(String(c.status));

    // Work on a copy sorted newest-first so iteration + grouping is deterministic.
    const sorted = [...calls].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    const out: Array<CallRecord & { _redialCount?: number; _groupedIds?: string[] }> = [];
    for (const c of sorted) {
      const last = out[out.length - 1];
      if (
        last &&
        isInboundMissed(c) &&
        isInboundMissed(last) &&
        (last.phoneNumber || '') === (c.phoneNumber || '') &&
        Math.abs(new Date(last.timestamp).getTime() - new Date(c.timestamp).getTime()) <= REDIAL_WINDOW_MS
      ) {
        last._redialCount = (last._redialCount || 1) + 1;
        last._groupedIds = [...(last._groupedIds || [last.id]), c.id];
        continue;
      }
      out.push({ ...c });
    }
    return out;
  }, [calls]);

  // Twilio device for callbacks
  const { connect: connectCall, isDeviceReady } = useTwilioDevice();
  const { openWidget, maximizeWidget, setActiveTab: setDialerTab } = useUnifiedDialer();

  // Get calls with recordings
  const callsWithRecordings = calls.filter(call => call.recordingUrl);
  const selectedCalls = calls.filter(call => selectedCallIds.has(call.id) && call.recordingUrl);
  const hasSelection = selectedCallIds.size > 0;

  // Toggle individual selection
  const toggleSelection = (callId: string) => {
    setSelectedCallIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedCallIds(new Set());
  };

  // Download single recording (used by the bulk-download loop). Resolves
  // false when the call has no recording or the JWT-authed fetch fails so
  // the batch progress counter can keep advancing without aborting.
  const downloadOneRecording = async (call: CallRecord): Promise<boolean> => {
    if (!call.recordingUrl) return false;
    try {
      const proxyUrl = getProxyRecordingUrl(call.recordingUrl) || call.recordingUrl;
      const timestamp = call.timestamp instanceof Date ? call.timestamp : (call.timestamp ? new Date(call.timestamp) : new Date());
      const validDate = isNaN(timestamp.getTime()) ? new Date() : timestamp;
      const dateStr = `${validDate.getFullYear()}-${String(validDate.getMonth() + 1).padStart(2, '0')}-${String(validDate.getDate()).padStart(2, '0')}`;
      const fileName = `recording-${call.contactName || call.phoneNumber || "unknown"}-${dateStr}.mp3`;
      await downloadRecording(proxyUrl, fileName);
      return true;
    } catch {
      return false;
    }
  };

  // Batch download handler
  const handleBatchDownload = async () => {
    if (selectedCallIds.size === 0) return;

    // Show confirmation for large batches
    if (selectedCallIds.size > 10 && !showConfirmDialog) {
      setShowConfirmDialog(true);
      return;
    }

    setShowConfirmDialog(false);
    setIsDownloading(true);
    setDownloadProgress({ current: 0, total: selectedCalls.length });

    const failures: string[] = [];
    let successCount = 0;

    for (let i = 0; i < selectedCalls.length; i++) {
      const call = selectedCalls[i];
      setDownloadProgress({ current: i + 1, total: selectedCalls.length });

      const success = await downloadOneRecording(call);

      if (success) {
        successCount++;
      } else {
        failures.push(call.contactName || call.phoneNumber || call.id);
      }

      // Add delay between downloads to avoid browser blocking
      if (i < selectedCalls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, DOWNLOAD_DELAY));
      }
    }

    setIsDownloading(false);
    setDownloadProgress({ current: 0, total: 0 });

    // Show results
    if (failures.length === 0) {
      toast({
        title: "Download Complete",
        description: `Successfully downloaded ${successCount} recording${successCount !== 1 ? 's' : ''}`,
      });
    } else {
      toast({
        title: "Download Completed with Errors",
        description: `Downloaded ${successCount} of ${selectedCalls.length} recordings. ${failures.length} failed.`,
        variant: "destructive",
      });
    }

    clearSelection();
  };
  const getStatusIcon = (
    status: CallRecord["status"],
    direction: CallRecord["direction"]
  ) => {
    const iconClass =
      direction === "inbound" ? "text-blue-600" : "text-green-600";

    switch (status) {
      case "answered":
        return <PhoneCall className={`h-4 w-4 ${iconClass}`} />;
      case "missed":
        return <PhoneMissed className="h-4 w-4 text-red-600" />;
      case "busy":
        return <PhoneOff className="h-4 w-4 text-orange-600" />;
      case "declined":
        return <PhoneOff className="h-4 w-4 text-orange-600" />;
      case "failed":
        return <PhoneOff className="h-4 w-4 text-red-600" />;
      default:
        return <Phone className={`h-4 w-4 ${iconClass}`} />;
    }
  };

  const getStatusBadge = (status: CallRecord["status"], direction?: string) => {
    const statusConfig = {
      answered: {
        variant: "default" as const,
        label: "Answered",
        className: "bg-green-100 text-green-800 border-green-200"
      },
      completed: {
        variant: "default" as const,
        label: "Answered",
        className: "bg-green-100 text-green-800 border-green-200"
      },
      missed: {
        variant: "destructive" as const,
        label: direction === "outbound" ? "No Answer" : "Missed",
        className: "bg-red-100 text-red-800 border-red-200"
      },
      busy: {
        variant: "secondary" as const,
        label: "Busy",
        className: "bg-orange-100 text-orange-800 border-orange-200"
      },
      declined: {
        variant: "secondary" as const,
        label: "Declined",
        className: "bg-red-100 text-red-800 border-red-200"
      },
      failed: {
        variant: "destructive" as const,
        label: "Failed",
        className: "bg-yellow-100 text-yellow-800 border-yellow-200"
      },
      voicemail: {
        variant: "secondary" as const,
        label: "Voicemail",
        className: "bg-purple-100 text-purple-800 border-purple-200"
      },
      "no-answer": {
        variant: "destructive" as const,
        label: "No Answer",
        className: "bg-red-100 text-red-800 border-red-200"
      },
      "no_answer": {
        variant: "destructive" as const,
        label: "No Answer",
        className: "bg-red-100 text-red-800 border-red-200"
      },
      canceled: {
        variant: "secondary" as const,
        label: "Canceled",
        className: "bg-gray-100 text-gray-800 border-gray-200"
      },
    } as const;

    const config = statusConfig[status] || {
      variant: "secondary" as const,
      label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
      className: ""
    };

    return (
      <Badge className={`text-xs ${config.className}`} variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const getRecordingStatus = (call: CallRecord): "available" | "processing" | "none" => {
    if (call.recordingUrl) {
      return "available";
    }
    // If the call was answered and happened within the last 5 minutes, recording might still be processing
    if (!call.timestamp) {
      return "none";
    }
    
    const callDate = call.timestamp instanceof Date ? call.timestamp : new Date(call.timestamp);
    // Check if date is valid
    if (isNaN(callDate.getTime())) {
      return "none";
    }
    
    const now = new Date();
    const minutesSinceCall = (now.getTime() - callDate.getTime()) / 1000 / 60;

    if (call.status === "answered" && call.duration > 0 && minutesSinceCall < 5) {
      return "processing";
    }

    return "none";
  };

  const getRecordingIndicator = (status: "available" | "processing" | "none") => {
    switch (status) {
      case "available":
        return <Mic className="h-3 w-3 text-green-600" />;
      case "processing":
        return <Loader2 className="h-3 w-3 animate-spin text-yellow-600" />;
      case "none":
        return null;
    }
  };

  const toggleCallExpanded = (callId: string) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  // Handle redial/callback for any call
  const handleCallBack = async (call: CallRecord) => {
    // For inbound calls, call back the caller (fromNumber). For outbound, redial the number that was called.
    const phoneToCall = call.direction === "inbound"
      ? (call.fromNumber || call.phoneNumber)
      : (call.phoneNumber || call.toNumber);

    if (!phoneToCall) {
      toast({
        title: "Cannot Call Back",
        description: "Phone number not available for this call.",
        variant: "destructive",
      });
      return;
    }

    if (!isDeviceReady) {
      toast({
        title: "Device Not Ready",
        description: "Please wait for the device to initialize.",
        variant: "destructive",
      });
      return;
    }

    setCallingBack(call.id);
    try {
      // Create session record FIRST to ensure database integrity (matches LeadActionButtons behavior)
      await api.post({
        url: getApiUrl('/communication/sessions/manual-call'),
        body: {
          toNumber: phoneToCall,
          leadId: call.leadId || undefined,
        },
        auth: true,
      });

      // Shell: surface the dialer sheet FIRST — on /m the call otherwise
      // connects invisibly (no bottom-right widget convention on a phone),
      // which read as "I pressed call back and nothing popped up".
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/m")) {
        openWidget();
        setDialerTab("call");
        maximizeWidget();
      }

      // Use TwilioDevice connect to show ActiveCallWidget at bottom-right
      await connectCall({
        to: phoneToCall,
        from: call.direction === "outbound" ? (call.fromNumber || undefined) : undefined,
        leadId: call.leadId || undefined,
        metadata: {
          contactName: call.contactName || undefined,
          lead_name: call.contactName || undefined,
          isCallback: true,
          source: "communication_center_callback",
        },
      });

      toast({
        title: call.direction === "inbound" ? "Calling Back" : "Redialing",
        description: `Calling ${call.contactName || phoneToCall}...`,
      });
    } catch (error) {
      console.error('[CallList] Failed to initiate call:', error);
      toast({
        title: "Call Failed",
        description: "Failed to initiate call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallingBack(null);
    }
  };

  if (calls.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No calls found.
      </div>
    );
  }

  return (
    <>
      {/* Batch Actions Toolbar */}
      {hasSelection && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedCallIds.size} recording{selectedCallIds.size !== 1 ? 's' : ''} selected
            </span>
            {isDownloading && (
              <span className="text-sm text-blue-700">
                Downloading {downloadProgress.current} of {downloadProgress.total}...
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleBatchDownload}
              disabled={isDownloading || selectedCallIds.size > MAX_BATCH_SIZE}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download Selected
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={clearSelection}
              disabled={isDownloading}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {selectedCallIds.size > MAX_BATCH_SIZE && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-900">
            Maximum batch size is {MAX_BATCH_SIZE} recordings. Please reduce your selection.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {groupedCalls.map((call) => {
          const recordingStatus = getRecordingStatus(call);
          const isExpanded = expandedCallId === call.id;
          const hasRecording = call.recordingUrl;
          const isSelected = selectedCallIds.has(call.id);
          const redialCount: number = (call as any)._redialCount || 1;

          return (
            <div key={call.id} className={`bg-card rounded-lg border ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
              <div
                className={`flex items-center justify-between p-4 ${hasRecording ? "cursor-pointer" : ""} hover:bg-accent/50 transition-colors`}
                onClick={() => hasRecording && toggleCallExpanded(call.id)}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* Checkbox for recordings */}
                  {hasRecording && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelection(call.id)}
                        disabled={isDownloading}
                        aria-label={`Select recording for ${call.contactName || call.phoneNumber}`}
                      />
                    </div>
                  )}
                  {getStatusIcon(call.status, call.direction)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-foreground truncate">
                      {call.contactName || formatPhoneNumber(call.phoneNumber)}
                    </p>
                    {getStatusBadge(call.status, call.direction)}
                    {redialCount > 1 && (
                      <Badge className="bg-primary/10 text-primary border-0 text-[10px] px-1.5 py-0 font-semibold">
                        ×{redialCount} redials
                      </Badge>
                    )}
                    {getRecordingIndicator(recordingStatus)}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {call.phoneNumber ? formatPhoneNumber(call.phoneNumber) : "No phone"}
                    </p>
                    <span className="text-xs text-muted-foreground">•</span>
                    <p className="text-xs text-muted-foreground">
                      {call.direction === "inbound" ? "Incoming" : "Outgoing"}
                    </p>
                    {/* Dialer mode (outbound only — an inbound call wasn't dialed). */}
                    {call.direction !== "inbound" && call.dialerMode && (
                      <span
                        className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border leading-none ${getDialerModeMeta(call.dialerMode).className}`}
                      >
                        {getDialerModeMeta(call.dialerMode).label}
                      </span>
                    )}
                    {/* Which of OUR numbers the call went through. Flips with direction:
                        outbound = dialled FROM, inbound = they dialled TO. On inbound,
                        `fromNumber` is the CALLER's number, already shown at the start of
                        this line, so printing it again would name the wrong party. */}
                    {(() => {
                      const isInbound = call.direction === "inbound";
                      const num = isInbound ? call.toNumber : call.fromNumber;
                      if (!num) return null;
                      return (
                        <>
                          <span className="text-xs text-muted-foreground">•</span>
                          <p
                            className="text-xs text-muted-foreground"
                            title={
                              isInbound
                                ? "Number they called"
                                : "Number this call was placed from"
                            }
                          >
                            {isInbound ? "to" : "from"} {formatPhoneNumber(num)}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  {/* Only show notes if they're not technical debugging info */}
                  {!!call.notes && !call.notes.startsWith('Call SID:') && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {call.notes}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {call.timestamp ? formatRelativeTime(call.timestamp) : 'Unknown time'}
                  </p>
                  <p className="text-xs font-medium text-foreground">
                    {formatDuration(call.duration)}
                  </p>
                </div>
                {!!call.leadId && (
                  <ViewLeadButton
                    leadId={call.leadId}
                    contactName={call.contactName || formatPhoneNumber(call.phoneNumber)}
                  />
                )}
                {/* Redial / Call back button */}
                <ViewerDisabled message="Upgrade to call back">
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCallBack(call);
                    }}
                    disabled={callingBack === call.id}
                    title={call.direction === "inbound" ? "Call back" : "Redial"}
                  >
                    {callingBack === call.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <PhoneOutgoing className="h-4 w-4" />
                    )}
                  </Button>
                </ViewerDisabled>
                {hasRecording && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCallExpanded(call.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
            {isExpanded && hasRecording && (
              <div className="px-4 pb-4">
                <RecordingPlayer
                  recording={toRecording({ ...call, recordingUrl: call.recordingUrl! })}
                  downloadName={`${(call.contactName || call.phoneNumber || "recording").replace(/\s+/g, "_")}_${new Date(call.timestamp).toISOString().slice(0, 10)}.mp3`}
                />
                <CallTranscriptPanel recording={toRecording({ ...call, recordingUrl: call.recordingUrl! })} />
              </div>
            )}
          </div>
        );
      })}
    </div>

    {/* Confirmation Dialog for Large Batches */}
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle>Download {selectedCallIds.size} Recordings?</DialogTitle>
          <DialogDescription>
            You are about to download {selectedCallIds.size} recordings. This may take a few moments and will download each file individually.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowConfirmDialog(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBatchDownload}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Download All
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
    </>
  );
}
